import * as React from "react";
import { cn } from "@/lib/utils";

interface RouteCardProps {
  title: string;
  description?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  testId?: string;
  /** Numbers the settings steps so they read in order. */
  step?: number;
  /** Scroll target for the route status's "fix it" link. */
  id?: string;
}

/** The card every section of the Form Routes dialog sits in. */
export function RouteCard({ title, description, className, children, testId, step, id }: RouteCardProps) {
  return (
    <section
      id={id}
      aria-label={title}
      data-testid={testId}
      className={cn(
        "bg-white border border-slate-200/80 rounded-2xl p-5 shadow-sm flex flex-col gap-4 scroll-mt-20",
        className
      )}
    >
      <div className="flex items-start gap-3">
        {step !== undefined && (
          <span
            aria-hidden
            className="flex size-6 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-bold text-primary"
          >
            {step}
          </span>
        )}
        <div className="flex flex-col gap-1">
          <h3 className="text-base font-semibold text-slate-800">{title}</h3>
          {description && <p className="text-sm text-slate-500 leading-relaxed">{description}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

/** Anchors the route status links to, so "Choose a form to finish" can take the admin there. */
export const STEP_IDS = {
  form: "form-routes-step-form",
  player: "form-routes-step-player",
  client: "form-routes-step-client",
  campaign: "form-routes-step-campaign",
} as const;

/** Uppercase label that groups the dialog's sections ("Adjust the route", "Check it"). */
export function SectionLabel({ children }: { children: React.ReactNode }) {
  return <h2 className="pt-2 text-xs font-bold uppercase tracking-wider text-slate-500">{children}</h2>;
}

interface ChoiceOption<T extends string> {
  value: T;
  label: string;
  description?: React.ReactNode;
  badge?: string;
}

interface ChoiceCardsProps<T extends string> {
  label: string;
  value: T;
  options: ChoiceOption<T>[];
  onChange: (value: T) => void;
  className?: string;
  /** Lay the cards out side by side from `md` up instead of stacking them. */
  horizontal?: boolean;
}

/** Radio cards: a label and the sentence that says when to pick it. */
export function ChoiceCards<T extends string>({
  label,
  value,
  options,
  onChange,
  className,
  horizontal = false,
}: ChoiceCardsProps<T>) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className={cn(
        horizontal
          ? cn("grid grid-cols-1 gap-2", options.length === 2 ? "md:grid-cols-2" : "md:grid-cols-3")
          : "flex flex-col gap-2",
        className
      )}
    >
      {options.map(option => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={() => onChange(option.value)}
            className={cn(
              "flex items-start gap-3 rounded-xl border px-3.5 py-3 text-left transition-colors cursor-pointer",
              checked
                ? "border-primary bg-primary/5 ring-1 ring-primary/30"
                : "border-slate-200 bg-white hover:bg-slate-50"
            )}
          >
            <span
              aria-hidden
              className={cn(
                "mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border",
                checked ? "border-primary bg-primary" : "border-slate-400 bg-white"
              )}
            >
              {checked && <span className="size-1.5 rounded-full bg-white" />}
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-800">
                {option.label}
                {option.badge && (
                  <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                    {option.badge}
                  </span>
                )}
              </span>
              {option.description && (
                <span className="text-xs text-slate-500 leading-relaxed">{option.description}</span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

interface SegmentedProps<T extends string> {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
}

/** A compact one-of-n switch ("TotalExpert | Another CRM | Website"). */
export function Segmented<T extends string>({ label, value, options, onChange }: SegmentedProps<T>) {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex rounded-lg bg-slate-100 p-[3px] gap-0.5">
      {options.map(option => {
        const checked = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={() => onChange(option.value)}
            className={cn(
              "rounded-md px-3 py-1 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap",
              checked ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-800"
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}

export function Helper({ children }: { children: React.ReactNode }) {
  return <p className="text-xs text-slate-500 leading-relaxed">{children}</p>;
}
